import React, { useState, useEffect } from "react";
import { useAuth } from "../../../../firebase/AuthContext";

export default function PromptGuide({ isEN, onLanguageChange }) {
  const { currentUser } = useAuth();
  const [selectedLanguage, setSelectedLanguage] = useState(isEN ? "en" : "zh");
  const [activeTab, setActiveTab] = useState("general");

  // Sync local state with parent's language changes
  useEffect(() => {
    setSelectedLanguage(isEN ? "en" : "zh");
  }, [isEN]);

  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
    if (onLanguageChange) {
      onLanguageChange(lang === "en");
    }
  };

  const tabs = [
    {
      id: "general",
      labelEN: "General",
      labelZH: "一般資訊",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      id: "product",
      labelEN: "Product",
      labelZH: "產品查詢",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      ),
    },
    {
      id: "category",
      labelEN: "Category",
      labelZH: "類別查詢",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },
  ];

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Header with icon */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {isEN ? "Access Required" : "需要存取權限"}
              </h2>
              <p className="text-orange-100 text-sm">
                {isEN ? "Troubleshooting Guide" : "故障排除指南"}
              </p>
            </div>

            {/* Content */}
            <div className="px-6 py-8">
              <div className="text-center mb-6">
                <h3 className="text-gray-800 dark:text-white font-semibold text-lg mb-3">
                  {isEN ? "Please log in to continue" : "請登入以繼續"}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  {isEN
                    ? "You need to be authenticated to access the troubleshooting guide and technical support resources."
                    : "您需要進行身份驗證才能存取故障排除指南和技術支援資源。"}
                </p>
              </div>

              {/* Features preview */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  {isEN ? "Common issues & solutions" : "常見問題和解決方案"}
                </div>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  {isEN ? "Step-by-step guides" : "逐步指南"}
                </div>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  {isEN ? "Direct support contact" : "直接支持聯繫"}
                </div>
              </div>

              {/* Login prompt */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <p className="text-center text-gray-700 dark:text-gray-300 text-sm">
                  {isEN
                    ? "Return to the main page to sign in with your account"
                    : "返回主頁面使用您的帳號登入"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-8">
        {/* <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                    <svg
                        className="w-6 h-6"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g
                            id="SVGRepo_tracerCarrier"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        ></g>
                        <g id="SVGRepo_iconCarrier">
                            {" "}
                            <path
                                d="M8 13V16H16V8H8V9"
                                stroke="#ffffff"
                                stroke-width="1.5"
                                stroke-linecap="round"
                            ></path>{" "}
                            <path
                                d="M16 16.001L19 16.0005C20.6569 16.0002 22.0002 17.3432 22.0005 19C22.0007 20.6569 20.6578 22.0002 19.0009 22.0005C17.3441 22.0007 16.0007 20.6578 16.0005 19.001L16 16.001Z"
                                stroke="#ffffff"
                                stroke-width="1.5"
                            ></path>{" "}
                            <path
                                d="M8.00096 16.001L5.00096 16.0005C3.34411 16.0002 2.00075 17.3432 2.00049 19C2.00023 20.6569 3.34316 22.0002 5.00002 22.0005C6.65687 22.0007 8.00023 20.6578 8.00049 19.001L8.00096 16.001Z"
                                stroke="#ffffff"
                                stroke-width="1.5"
                            ></path>{" "}
                            <path
                                d="M16 8.00002L19 8.00049C20.6569 8.00075 22.0002 6.65781 22.0005 5.00096C22.0007 3.34411 20.6578 2.00075 19.0009 2.00049C17.3441 2.00023 16.0007 3.34316 16.0005 5.00002L16 8.00002Z"
                                stroke="#ffffff"
                                stroke-width="1.5"
                            ></path>{" "}
                            <path
                                d="M8.00096 8.00002L5.00096 8.00049C3.34411 8.00075 2.00075 6.65781 2.00049 5.00096C2.00023 3.34411 3.34316 2.00075 5.00002 2.00049C6.65687 2.00023 8.00023 3.34316 8.00049 5.00002L8.00096 8.00002Z"
                                stroke="#ffffff"
                                stroke-width="1.5"
                            ></path>{" "}
                        </g>
                    </svg>
                </div> */}
        <div>
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white">
            {isEN ? "Prompt Guide" : "提示指南"}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {isEN
              ? "Prompting Guide for effective interactions"
              : "有效互動的提示指南"}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-slate-200 dark:bg-gray-900/40 rounded-lg p-6 border border-white dark:border-gray-700 shadow-lg">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center mr-3 shadow-md">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white">
              {isEN ? "Crafting Effective Prompts" : "編寫有效的提示"}
            </h3>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            {isEN
              ? "To get the best results from your questions, it's important to craft an effective prompt. This guide will help you learn how to create prompts that yield the best responses from the AI assistant."
              : "要從您的問題中獲得最佳結果，編寫有效的提示非常重要。本指南將幫助您學習如何創建能夠從AI助手獲得最佳回應的提示。"}
          </p>

          <div className="grid gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-300 dark:border-gray-700 shadow-sm">
              <div className="flex items-center mb-2">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-2 text-white text-sm font-bold">
                  1
                </div>
                <h4 className="text-gray-800 dark:text-white font-semibold">
                  {isEN ? "Be Clear and Specific" : "清晰且具體"}
                </h4>
              </div>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                {isEN
                  ? "Clearly define what attributes and products you are querying about. Avoid vague language."
                  : "清楚地定義您查詢的屬性和產品。避免使用模糊的語言。"}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-300 dark:border-gray-700 shadow-sm">
              <div className="flex items-center mb-2">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-2 text-white text-sm font-bold">
                  2
                </div>
                <h4 className="text-gray-800 dark:text-white font-semibold">
                  {isEN ? "Follow our trained Intent" : "遵循我們訓練的意圖"}
                </h4>
              </div>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                {isEN
                  ? "Our AI is trained on specific intents like 'Product Inquiry', 'Order Status', and 'Technical Support'. Make sure your prompt aligns with these intents for optimal results."
                  : "我們的AI經過特定意圖的訓練，例如「產品查詢」、「訂單狀態」和「技術支援」。確保您的提示與這些意圖保持一致，以獲得最佳結果。"}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-300 dark:border-gray-700 shadow-sm">
              <div className="flex items-center mb-2">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-2 text-white text-sm font-bold">
                  3
                </div>
                <h4 className="text-gray-800 dark:text-white font-semibold">
                  {isEN ? "Keep it Concise" : "保持簡潔"}
                </h4>
              </div>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                {isEN
                  ? "Avoid overly long prompts. Be concise while still providing necessary details."
                  : "避免過長的提示。在提供必要細節的同時保持簡潔。"}
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-300 dark:border-gray-700 shadow-sm">
            <div className="flex items-center mb-3">
              {/* <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3 shadow-md">
                                <svg
                                    className="w-5 h-5 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                                    />
                                </svg>
                            </div> */}
              <h4 className="text-xl font-semibold text-gray-800 dark:text-white">
                {isEN ? "Language Support" : "語言支援"}
              </h4>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
              {isEN
                ? "Our AI model is trained to answer questions in both English and Traditional Chinese. Choose your preferred language for optimal prompting experience."
                : "我們的AI模型經過訓練，可以回答英語和繁體中文的問題。選擇您的首選語言以獲得最佳的提示體驗。"}
            </p>

            <div className="flex items-center justify-center">
              <div className="inline-flex items-center bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleLanguageChange("en")}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                    selectedLanguage === "en"
                      ? "bg-orange-500 text-white shadow-md transform scale-105"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  <span>English</span>
                </button>
                <button
                  onClick={() => handleLanguageChange("zh")}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                    selectedLanguage === "zh"
                      ? "bg-orange-500 text-white shadow-md transform scale-105"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  <span>繁體中文</span>
                </button>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="mt-8">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === tab.id
                          ? "border-orange-500 text-orange-600 dark:text-orange-400"
                          : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500"
                      }`}
                    >
                      {tab.icon}
                      <span>{isEN ? tab.labelEN : tab.labelZH}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {!isEN && (
              <div className="bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-400 dark:border-yellow-700 rounded-lg p-4 my-6">
                <div className="flex items-center">
                  {/* <div className="text-yellow-600 mr-3">
                                        <svg
                                            className="w-5 h-5"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div> */}
                  <div>
                    <h3 className="text-yellow-800 dark:text-yellow-200 font-medium">
                      中文版本即將推出
                    </h3>
                    <div className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                      我們正在優化中文翻譯，請暫時使用英文版本以獲得最佳體驗。
                      <hr className="my-2 border-yellow-300 dark:border-yellow-800" />
                      We are working on improving the Chinese translation,
                      please use the English version for the best experience.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Content */}
            {isEN && (
              <div className="mt-6">
                {activeTab === "general" && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="flex items-center mb-4">
                      {/* <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center mr-3">
                                                <svg
                                                    className="w-5 h-5 text-white"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                            </div> */}
                      <h4 className="text-lg font-semibold text-gray-800 dark:text-white">
                        {isEN ? "General Information Examples" : "一般資訊範例"}
                      </h4>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h5 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                            1
                          </span>
                          {isEN
                            ? "General Info Intent examples:"
                            : "詢問一般資訊意圖範例："}
                        </h5>
                        <div className="bg-gray-600 rounded-lg p-4 text-white">
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  List the product names of{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal pad
                                  </span>
                                </>
                              ) : (
                                <>列出導熱墊的產品名稱</>
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  explain{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    graphite sheet
                                  </span>
                                </>
                              ) : (
                                "解釋石墨片"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  how do you measure{" "}
                                  <span className="bg-pink-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal conductivity
                                  </span>
                                </>
                              ) : (
                                "如何測量熱導率"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  what is{" "}
                                  <span className="bg-green-500 inline-block mx-1 px-1 rounded text-white">
                                    ASTM D5470
                                  </span>{" "}
                                  test
                                </>
                              ) : (
                                "告訴我延伸率的含義"
                              )}
                            </li>
                          </ul>
                        </div>
                        <div className="p-4 flex flex-col">
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-blue-500"></div>
                            <span className="text-sm">
                              Product Category (thermal pad, thermal paste,
                              etc.)
                            </span>
                          </span>
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-pink-500"></div>
                            <span className="text-sm">
                              Product Attributes (thermal conductivity,
                              thickness, viscosity, etc.)
                            </span>
                          </span>
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-green-500"></div>
                            <span className="text-sm">Testing Methods</span>
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h5 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                            2
                          </span>
                          {isEN ? "Product Features" : "產品特徵"}
                        </h5>
                        <div className="bg-gray-600 rounded-lg p-4 text-white">
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  suggest the pad with{" "}
                                  <span className="bg-yellow-500 inline-block mx-1 px-1 rounded text-white">
                                    low hardness
                                  </span>
                                </>
                              ) : (
                                "建議使用低硬度的導熱墊"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  which pad is best as a{" "}
                                  <span className="bg-yellow-500 inline-block mx-1 px-1 rounded text-white">
                                    buffer in electronics
                                  </span>
                                </>
                              ) : (
                                "哪種導熱墊最適合作為電子產品中的緩衝墊"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  list of products with{" "}
                                  <span className="bg-yellow-500 inline-block mx-1 px-1 rounded text-white">
                                    low oil-bleed
                                  </span>
                                </>
                              ) : (
                                "低油滲透的產品列表"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  what product has
                                  <span className="bg-yellow-500 inline-block mx-1 px-1 rounded text-white">
                                    high thermal conductivity?
                                  </span>
                                </>
                              ) : (
                                "哪種產品具有高導熱性？"
                              )}
                            </li>
                          </ul>
                        </div>
                        <div className="p-4 flex flex-col">
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-yellow-500"></div>
                            <span className="text-sm">
                              Features Name/ Tags (low mass, good electrical
                              insulation, etc.)
                            </span>
                          </span>
                        </div>
                      </div>

                      {/* <div className="bg-gray-50 rounded-lg p-4">
                                                <h5 className="text-gray-700 font-semibold mb-3 flex items-center">
                                                    <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                                                        2
                                                    </span>
                                                    {isEN
                                                        ? "Product Comparison examples:"
                                                        : "產品比較範例："}
                                                </h5>
                                                <div className="bg-gray-600 rounded-lg p-4 text-white">
                                                    <ul className="space-y-2 text-sm">
                                                        <li className="flex items-start">
                                                            <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                                            {isEN ? (
                                                                <>
                                                                    compare the
                                                                    features of{" "}
                                                                    <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                                                        thermal
                                                                        pad{" "}
                                                                    </span>
                                                                    and{" "}
                                                                    <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                                                        ultra
                                                                        soft
                                                                        thermal
                                                                        pads?
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                "比較導熱墊和超軟導熱墊的特徵？"
                                                            )}
                                                        </li>
                                                        <li className="flex items-start">
                                                            <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                                            {isEN ? (
                                                                <>
                                                                    compare the
                                                                    features of{" "}
                                                                    <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                                                        TG-A6050
                                                                    </span>{" "}
                                                                    and{" "}
                                                                    <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                                                        TG-A6200
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                "比較TG-A6050和TG-A6200的特徵"
                                                            )}
                                                        </li>
                                                        <li className="flex items-start">
                                                            <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                                            {isEN ? (
                                                                <>
                                                                    please
                                                                    compare the{" "}
                                                                    <span className="bg-pink-500 inline-block mx-1 px-1 rounded text-white">
                                                                        specs
                                                                    </span>{" "}
                                                                    of{" "}
                                                                    <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                                                        thermal
                                                                        putty
                                                                    </span>{" "}
                                                                    and{" "}
                                                                    <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                                                        thermal
                                                                        pads
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                "請比較導熱膏和導熱墊的規格"
                                                            )}
                                                        </li>
                                                        <li className="flex items-start">
                                                            <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                                            {isEN ? (
                                                                <>
                                                                    which
                                                                    product is
                                                                    best to use
                                                                    in{" "}
                                                                    <span className="bg-gray-100 inline-block mx-1 px-1 rounded text-black">
                                                                        5G
                                                                    </span>{" "}
                                                                    in{" "}
                                                                    <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                                                        thermal
                                                                        pads
                                                                    </span>
                                                                    ?
                                                                </>
                                                            ) : (
                                                                "哪種導熱墊最適合用於5G？"
                                                            )}
                                                        </li>
                                                    </ul>
                                                </div>
                                                <div className="p-4 flex flex-col">
                                                    <span className="flex flex-row gap-2 items-center">
                                                        <div className="h-4 w-4 rounded bg-blue-500"></div>
                                                        <span className="text-sm">
                                                            Product Category
                                                            (thermal pad,
                                                            thermal paste, etc.)
                                                        </span>
                                                    </span>
                                                    <span className="flex flex-row gap-2 items-center">
                                                        <div className="h-4 w-4 rounded bg-purple-500"></div>
                                                        <span className="text-sm">
                                                            Product Name
                                                            (TG-A3500, TG-A4040,
                                                            etc.)
                                                        </span>
                                                    </span>
                                                    <span className="flex flex-row gap-2 items-center">
                                                        <div className="h-4 w-4 rounded bg-pink-500"></div>
                                                        <span className="text-sm">
                                                            Product Attributes
                                                            (thermal
                                                            conductivity,
                                                            thickness,
                                                            viscosity, etc.)
                                                        </span>
                                                    </span>
                                                    <span className="flex flex-row gap-2 items-center">
                                                        <div className="h-4 w-4 rounded border border-black bg-gray-100"></div>
                                                        <span className="text-sm">
                                                            Testing Methods
                                                        </span>
                                                    </span>
                                                </div>
                                            </div> */}
                    </div>
                  </div>
                )}

                {activeTab === "product" && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="flex items-center mb-6">
                      {/* <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center mr-3">
                                                <svg
                                                    className="w-5 h-5 text-white"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                                    />
                                                </svg>
                                            </div> */}
                      <h4 className="text-lg font-semibold text-gray-800 dark:text-white">
                        {isEN ? "Product Inquiry Examples" : "產品查詢範例"}
                      </h4>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h5 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                            1
                          </span>
                          {isEN ? "Product Features" : "產品功能"}
                        </h5>
                        <div className="bg-gray-600 rounded-lg p-4 text-white">
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  What are the key features of
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A3500
                                  </span>
                                  ?
                                </>
                              ) : (
                                "TG-A3500的主要功能有哪些？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Show the features of{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A4040
                                  </span>
                                </>
                              ) : (
                                "顯示TG-A4040的功能"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Tell me about the key functions of{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A6200
                                  </span>
                                </>
                              ) : (
                                "告訴我TG-A6200的主要功能"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Provide a feature summary of{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A1450
                                  </span>
                                </>
                              ) : (
                                "提供TG-A1450的功能摘要"
                              )}
                            </li>
                          </ul>
                        </div>
                        <div className="p-4 flex flex-col">
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-purple-500"></div>
                            <span className="text-sm">
                              Product Name (TG-A3500, TG-A4040, etc.)
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h5 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                            2
                          </span>
                          {isEN ? "Product Overview" : "產品概述"}
                        </h5>
                        <div className="bg-gray-600 rounded-lg p-4 text-white">
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Give me the summary of{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A4040
                                  </span>
                                </>
                              ) : (
                                "給我TG-A4040的摘要"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Tell me the overview of{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A1780
                                  </span>
                                </>
                              ) : (
                                "顯示TG-A1780的概述"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Is{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG2030
                                  </span>{" "}
                                  an upgrade over{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A3500
                                  </span>
                                  ?
                                </>
                              ) : (
                                "TG2030是否是TG-A3500的升級版？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Where does{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A6050
                                  </span>{" "}
                                  fit in our product lineup?
                                </>
                              ) : (
                                "TG-A6050在我們的產品線中適合什麼？"
                              )}
                            </li>
                          </ul>
                        </div>
                        <div className="p-4 flex flex-col">
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-purple-500"></div>
                            <span className="text-sm">
                              Product Name (TG-A3500, TG-A4040, etc.)
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h5 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                            3
                          </span>
                          {isEN ? "Product Applications" : "產品應用"}
                        </h5>
                        <div className="bg-gray-600 rounded-lg p-4 text-white">
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  What are the{" "}
                                  <span className="bg-gray-100 inline-block mx-1 px-1 rounded text-black">
                                    applications
                                  </span>{" "}
                                  of{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-AD66
                                  </span>
                                  ?
                                </>
                              ) : (
                                "TG-AD66的應用有哪些？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Is{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A126X
                                  </span>
                                  recommended for
                                  <span className="bg-gray-100 inline-block mx-1 px-1 rounded text-black">
                                    medical
                                  </span>{" "}
                                  devices?
                                </>
                              ) : (
                                "TG-A126X是否建議用於醫療設備？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Does{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG4040F
                                  </span>
                                  work well in
                                  <span className="bg-gray-100 inline-block mx-1 px-1 rounded text-black">
                                    EVs
                                  </span>
                                  ?
                                </>
                              ) : (
                                "TG4040F在電動車中表現如何？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  What{" "}
                                  <span className="bg-gray-100 inline-block mx-1 px-1 rounded text-black">
                                    industries
                                  </span>{" "}
                                  use{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A6050
                                  </span>
                                  ?
                                </>
                              ) : (
                                "TG-A6050適用於哪些行業？"
                              )}
                            </li>
                          </ul>
                        </div>
                        <div className="p-4 flex flex-col">
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-purple-500"></div>
                            <span className="text-sm">
                              Product Name (TG-A3500, TG-A4040, etc.)
                            </span>
                          </span>
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded border border-black bg-gray-100"></div>
                            <span className="text-sm">Testing Methods</span>
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h5 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                            4
                          </span>
                          {isEN ? "Product Specifications" : "產品規格"}
                        </h5>
                        <div className="bg-gray-600 rounded-lg p-4 text-white">
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  What are the specifications of{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A730AB
                                  </span>
                                  ?
                                </>
                              ) : (
                                "TG-A730AB的規格有哪些？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Show me the
                                  <span className="bg-pink-500 inline-block mx-1 px-1 rounded text-white">
                                    thickness
                                  </span>{" "}
                                  of
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-AD75
                                  </span>
                                </>
                              ) : (
                                "TG-AD75的厚度是多少？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  How much is the
                                  <span className="bg-pink-500 inline-block mx-1 px-1 rounded text-white">
                                    density
                                  </span>{" "}
                                  of
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-AD75
                                  </span>
                                  ?
                                </>
                              ) : (
                                "TG-AD75的密度是多少？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Tell me the
                                  <span className="bg-pink-500 inline-block mx-1 px-1 rounded text-white">
                                    color
                                  </span>{" "}
                                  of{" "}
                                  <span className="bg-purple-500 inline-block mx-1 px-1 rounded text-white">
                                    TG-A1450
                                  </span>
                                  .
                                </>
                              ) : (
                                "TG-A1450的顏色是什麼？"
                              )}
                            </li>
                          </ul>
                        </div>
                        <div className="p-4 flex flex-col">
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-purple-500"></div>
                            <span className="text-sm">
                              Product Name (TG-A3500, TG-A4040, etc.)
                            </span>
                          </span>
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-pink-500"></div>
                            <span className="text-sm">
                              Product Attributes (thermal conductivity,
                              thickness, viscosity, etc.)
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "category" && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="flex items-center mb-6">
                      {/* <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center mr-3">
                                                <svg
                                                    className="w-5 h-5 text-white"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                                    />
                                                </svg>
                                            </div> */}
                      <h4 className="text-lg font-semibold text-gray-800 dark:text-white">
                        {isEN ? "Category Inquiry Examples" : "類別查詢範例"}
                      </h4>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h5 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                            1
                          </span>
                          {isEN ? "Category Features" : "類別功能"}
                        </h5>
                        <div className="bg-gray-600 rounded-lg p-4 text-white">
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  what are the
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal putty
                                  </span>
                                  features?
                                </>
                              ) : (
                                "導熱泥的特徵是什麼"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  which{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal pad{" "}
                                  </span>
                                  has{" "}
                                  <span className="bg-yellow-500 inline-bloxk mx-1 px-1 rounded text-white">
                                    high breakdown voltage
                                  </span>
                                </>
                              ) : (
                                "哪種導熱墊具有高擊穿電壓"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  tell me the name of{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal conductive insulator
                                  </span>{" "}
                                  which is{" "}
                                  <span className="bg-yellow-500 inline-block mx-1 px-1 rounded text-white">
                                    Easy to assemble
                                  </span>
                                </>
                              ) : (
                                "告訴我易於組裝的導熱絕緣體的名稱"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Tell me the features of
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    Thermal pad
                                  </span>
                                </>
                              ) : (
                                "告訴我導熱墊的特徵"
                              )}
                            </li>
                          </ul>
                        </div>
                        <div className="p-4 flex flex-col">
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-blue-500"></div>
                            <span className="text-sm">
                              Product Category (thermal pad, thermal paste,
                              etc.)
                            </span>
                          </span>
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-yellow-500"></div>
                            <span className="text-sm">
                              Features Name/ Tags (low mass, good electrical
                              insulation, etc.)
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h5 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                            2
                          </span>
                          {isEN ? "Category Properties" : "類別屬性"}
                        </h5>
                        <div className="bg-gray-600 rounded-lg p-4 text-white">
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  what are the specifications of{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal pad
                                  </span>
                                  ?
                                </>
                              ) : (
                                "導熱墊的規格有哪些？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  What is the
                                  <span className="bg-yellow-500 inline-block mx-1 px-1 rounded text-white">
                                    density
                                  </span>{" "}
                                  of
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal tape
                                  </span>
                                  ?
                                </>
                              ) : (
                                "導熱膠帶的密度是多少？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  have{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    graphene
                                  </span>
                                  products a{" "}
                                  <span className="bg-yellow-500 inline-block mx-1 px-1 rounded text-white">
                                    high Dielectric Breakdown Voltage
                                  </span>{" "}
                                  ?
                                </>
                              ) : (
                                "石墨烯產品的介電擊穿電壓高嗎？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  What is the typical
                                  <span className="bg-yellow-500 inline-block mx-1 px-1 rounded text-white">
                                    operating temperature
                                  </span>{" "}
                                  of
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal pads
                                  </span>
                                  ?
                                </>
                              ) : (
                                "導熱墊的典型工作溫度是多少？"
                              )}
                            </li>
                          </ul>
                        </div>
                        <div className="p-4 flex flex-col">
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-blue-500"></div>
                            <span className="text-sm">
                              Product Category (thermal pad, thermal paste,
                              etc.)
                            </span>
                          </span>
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-yellow-500"></div>
                            <span className="text-sm">
                              Features Name/ Tags (low mass, good electrical
                              insulation, etc.)
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h5 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                            3
                          </span>
                          {isEN ? "Category Overview" : "類別概述"}
                        </h5>
                        <div className="bg-gray-600 rounded-lg p-4 text-white">
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  what are specs of{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal putty?
                                  </span>
                                </>
                              ) : (
                                "導熱泥的特徵是什麼？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  I need a quick summary of high performance{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal pad.
                                  </span>
                                </>
                              ) : (
                                "我需要一個高性能導熱墊的快速總結。"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  what is{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    potting compound?
                                  </span>
                                </>
                              ) : (
                                "什麼是灌封膠？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Tell me the overview of{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    Thermal putty
                                  </span>
                                </>
                              ) : (
                                "告訴我導熱泥的概述"
                              )}
                            </li>
                          </ul>
                        </div>
                        <div className="p-4 flex flex-col">
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-blue-500"></div>
                            <span className="text-sm">
                              Product Category (thermal pad, thermal paste,
                              etc.)
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h5 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                            4
                          </span>
                          {isEN ? "Category Applications" : "類別應用"}
                        </h5>
                        <div className="bg-gray-600 rounded-lg p-4 text-white">
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  give me list of
                                  <span className="bg-gray-100 inline-block mx-1 px-1 rounded text-black">
                                    applications
                                  </span>{" "}
                                  of{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal putty
                                  </span>
                                  ?
                                </>
                              ) : (
                                "告訴我導熱泥的應用列表？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  Does{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal paste{" "}
                                  </span>
                                  work well in
                                  <span className="bg-gray-100 inline-block mx-1 px-1 rounded text-black">
                                    EVs?
                                  </span>
                                </>
                              ) : (
                                "導熱膏在電動車中表現如何？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  What systems use
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    thermal pads
                                  </span>{" "}
                                  ?
                                </>
                              ) : (
                                "什麼系統使用導熱墊？"
                              )}
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              {isEN ? (
                                <>
                                  what are the
                                  <span className="bg-gray-100 inline-block mx-1 px-1 rounded text-black">
                                    applications
                                  </span>{" "}
                                  of{" "}
                                  <span className="bg-blue-500 inline-block mx-1 px-1 rounded text-white">
                                    Thermal putty
                                  </span>
                                </>
                              ) : (
                                "導熱泥的應用有哪些？"
                              )}
                            </li>
                          </ul>
                        </div>
                        <div className="p-4 flex flex-col">
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded bg-blue-500"></div>
                            <span className="text-sm">
                              Product Category (thermal pad, thermal paste,
                              etc.)
                            </span>
                          </span>
                          <span className="flex flex-row gap-2 items-center">
                            <div className="h-4 w-4 rounded border border-black bg-gray-100"></div>
                            <span className="text-sm">Testing Methods</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
